import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, ArrowRight, Loader2, AlertCircle, Lock, Check } from 'lucide-react';
import { api, initiatePayment } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';

const CITIES = ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Chandigarh', 'Goa', 'Kochi', 'Indore', 'Vizag', 'Other'];

const CITY_AREAS = {
  Mumbai: ['Andheri West', 'Andheri East', 'Bandra West', 'Bandra East', 'Juhu', 'Powai', 'Lower Parel', 'BKC', 'Worli', 'Colaba', 'Fort', 'Churchgate', 'Dadar', 'Kurla', 'Malad', 'Goregaon', 'Borivali', 'Kandivali', 'Thane', 'Navi Mumbai', 'Vashi', 'Kharghar', 'Santacruz', 'Khar', 'Versova', 'Lokhandwala', 'Oshiwara', 'Matunga', 'Prabhadevi', 'Other'],
  Delhi: ['Connaught Place', 'Hauz Khas', 'Khan Market', 'Saket', 'Greater Kailash', 'Defence Colony', 'Lajpat Nagar', 'Karol Bagh', 'Rajouri Garden', 'Dwarka', 'Janakpuri', 'Rohini', 'Pitampura', 'Vasant Kunj', 'Vasant Vihar', 'Green Park', 'Nehru Place', 'Chandni Chowk', 'Paharganj', 'Aerocity', 'Cyber Hub Gurgaon', 'DLF Phase', 'Golf Course Road', 'Sector 29 Gurgaon', 'Noida Sector 18', 'Noida Sector 62', 'Other'],
  Bangalore: ['Koramangala', 'Indiranagar', 'HSR Layout', 'Whitefield', 'JP Nagar', 'Jayanagar', 'BTM Layout', 'Electronic City', 'Marathahalli', 'Bellandur', 'Sarjapur Road', 'MG Road', 'Brigade Road', 'Church Street', 'Lavelle Road', 'Malleshwaram', 'Rajajinagar', 'Sadashivanagar', 'Yelahanka', 'Bannerghatta Road', 'Hennur', 'Hebbal', 'Kalyan Nagar', 'Other'],
  Hyderabad: ['Jubilee Hills', 'Banjara Hills', 'Madhapur', 'Hitech City', 'Gachibowli', 'Kondapur', 'Kukatpally', 'Ameerpet', 'Begumpet', 'Secunderabad', 'Somajiguda', 'Tolichowki', 'Manikonda', 'Film Nagar', 'Nampally', 'Charminar', 'Dilsukhnagar', 'LB Nagar', 'Miyapur', 'Kompally', 'Shamshabad', 'Other'],
  Chennai: ['T Nagar', 'Anna Nagar', 'Adyar', 'Velachery', 'Mylapore', 'Nungambakkam', 'Alwarpet', 'Besant Nagar', 'ECR', 'OMR', 'Porur', 'Vadapalani', 'Guindy', 'Egmore', 'Kilpauk', 'Royapettah', 'Thiruvanmiyur', 'Sholinganallur', 'Perungudi', 'Other'],
  Pune: ['Koregaon Park', 'Kalyani Nagar', 'Viman Nagar', 'Baner', 'Aundh', 'Hinjewadi', 'Kothrud', 'FC Road', 'JM Road', 'MG Road Pune', 'Wakad', 'Pimple Saudagar', 'Hadapsar', 'Kharadi', 'Magarpatta', 'Camp', 'Deccan', 'Shivaji Nagar', 'Sinhagad Road', 'Other'],
  Kolkata: ['Park Street', 'Salt Lake', 'New Town', 'Ballygunge', 'Alipore', 'Esplanade', 'Gariahat', 'Howrah', 'Dum Dum', 'Rajarhat', 'EM Bypass', 'Behala', 'Jadavpur', 'Tollygunge', 'Elgin Road', 'Camac Street', 'Other'],
  Ahmedabad: ['SG Highway', 'Prahlad Nagar', 'Navrangpura', 'CG Road', 'Ashram Road', 'Bodakdev', 'Satellite', 'Vastrapur', 'Manek Chowk', 'Law Garden', 'Thaltej', 'Bopal', 'Chandkheda', 'Gota', 'Other'],
  Jaipur: ['C Scheme', 'Vaishali Nagar', 'Malviya Nagar', 'Raja Park', 'Tonk Road', 'MI Road', 'Mansarovar', 'Jagatpura', 'Sitapura', 'Amer Road', 'Bapu Nagar', 'Other'],
  Lucknow: ['Hazratganj', 'Gomti Nagar', 'Aliganj', 'Indira Nagar', 'Aminabad', 'Alambagh', 'Mahanagar', 'Vikas Nagar', 'Rajajipuram', 'Other'],
  Chandigarh: ['Sector 17', 'Sector 22', 'Sector 26', 'Sector 35', 'Sector 7', 'Sector 8', 'Sector 9', 'Sector 43', 'Elante Mall Area', 'IT Park', 'Other'],
  Goa: ['Panjim', 'Calangute', 'Baga', 'Anjuna', 'Vagator', 'Candolim', 'Assagao', 'Mapusa', 'Margao', 'Vasco', 'Morjim', 'Arambol', 'Porvorim', 'Other'],
  Kochi: ['MG Road Kochi', 'Marine Drive', 'Edappally', 'Kakkanad', 'Fort Kochi', 'Mattancherry', 'Palarivattom', 'Vyttila', 'Panampilly Nagar', 'Kaloor', 'Other'],
  Indore: ['Vijay Nagar', 'Palasia', 'Sapna Sangeeta', 'South Tukoganj', 'Sarwate Bus Stand Area', 'Rau', 'Bhawarkuan', 'AB Road', 'Other'],
  Vizag: ['Beach Road', 'Dwaraka Nagar', 'MVP Colony', 'Siripuram', 'Rushikonda', 'Madhurawada', 'Gajuwaka', 'Seethammadhara', 'Other'],
  Other: ['City Center', 'Main Market', 'Commercial Area', 'Highway', 'Residential Area', 'IT Park Area', 'Other'],
};
const CUISINES = ['North Indian', 'South Indian', 'Chinese/Indo-Chinese', 'Italian', 'Continental', 'Pan-Asian', 'Street Food', 'Biryani', 'Pizza/Burger', 'Cafe/Bakery', 'Desserts', 'Multi-Cuisine', 'Regional', 'Health Food', 'Other'];

const MODEL_TYPES = [
  { value: 'qsr', label: 'QSR (Quick Service)' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'casual_dining', label: 'Casual Dining' },
  { value: 'fine_dining', label: 'Fine Dining' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'hybrid', label: 'Hybrid Model' },
];

const STEP_TITLES = ['Your Concept', 'Investment', 'Operations', 'Revenue'];

// Extracted OUTSIDE the component to prevent remount on every render
function FieldError({ errors, field }) {
  if (!errors[field]) return null;
  return (
    <p className="text-xs text-red-500 flex items-center gap-1 mt-1" data-testid={`error-${field}`}>
      <AlertCircle className="h-3 w-3" /> {errors[field]}
    </p>
  );
}

function FormInput({ label, field, prefix, placeholder, type = "number", required, value, onChange, error }) {
  return (
    <div className="space-y-2">
      <Label className="text-[#1D1D1F] text-sm font-medium">
        {label} {required && <span className="text-red-400">*</span>}
      </Label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B] text-sm">{prefix}</span>}
        <Input
          data-testid={`input-${field}`}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`bg-white border-[#E5E5EA] rounded-2xl h-12 text-[#1D1D1F] focus:ring-[#0071E3] ${prefix ? 'pl-8' : ''} ${error ? 'border-red-400' : ''}`}
        />
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
          <AlertCircle className="h-3 w-3" /> {error}
        </p>
      )}
    </div>
  );
}

const defaultForm = {
  // Personal details
  contact_name: '', contact_phone: '', contact_email: '', contact_location: '',
  // Concept
  city: '', area: '', custom_area: '', area_type: '', spending_level: '', audience_type: '',
  concept_description: '', cuisine: '', aov: '', seating_capacity: '',
  preferred_model: '',
  dine_in_pct: 50, delivery_pct: 30, takeaway_pct: 20,
  rent: '', deposit: '', interiors: '', kitchen_setup: '', licenses: '', opening_inventory: '', working_capital_months: 3,
  staff_count: '', avg_salary: '', food_cost_pct: 30, packaging_pct: 5, electricity: '', marketing: '', aggregator_dependence_pct: 0, menu_category_count: 5,
  daily_orders: '', table_turns: 2, operating_days: 26,
};

export default function Assessment() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPaywall, setShowPaywall] = useState(false);
  const [customAreas, setCustomAreas] = useState([]);

  // Load custom areas when city changes
  useEffect(() => {
    if (!form.city) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/areas/${encodeURIComponent(form.city)}`)
      .then(r => r.ok ? r.json() : [])
      .then(areas => setCustomAreas(areas))
      .catch(() => setCustomAreas([]));
  }, [form.city]);

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.city) e.city = 'Select a city';
      if (!form.area) e.area = 'Select an area';
      if (form.area === 'Other' && !form.custom_area?.trim()) e.area = 'Enter the area name';
      if (!form.cuisine) e.cuisine = 'Select a cuisine';
      if (!form.concept_description?.trim()) e.concept_description = 'Describe your concept briefly';
      if (!form.aov || Number(form.aov) <= 0) e.aov = 'Enter average order value';
      if (!form.seating_capacity && form.seating_capacity !== 0) e.seating_capacity = 'Enter seating capacity';
      if (!form.area_type) e.area_type = 'Select area type';
      if (!form.spending_level) e.spending_level = 'Select spending level';
      if (!form.audience_type) e.audience_type = 'Select audience type';
    }
    if (s === 1) {
      if (!form.rent || Number(form.rent) <= 0) e.rent = 'Enter monthly rent';
      if (!form.deposit || Number(form.deposit) <= 0) e.deposit = 'Enter security deposit';
      if (!form.interiors || Number(form.interiors) <= 0) e.interiors = 'Enter interiors cost';
      if (!form.kitchen_setup || Number(form.kitchen_setup) <= 0) e.kitchen_setup = 'Enter kitchen setup cost';
      if (!form.licenses || Number(form.licenses) <= 0) e.licenses = 'Enter licenses cost';
      if (!form.opening_inventory || Number(form.opening_inventory) <= 0) e.opening_inventory = 'Enter opening inventory';
    }
    if (s === 2) {
      if (!form.staff_count || Number(form.staff_count) <= 0) e.staff_count = 'Enter staff count';
      if (!form.avg_salary || Number(form.avg_salary) <= 0) e.avg_salary = 'Enter average salary';
      if (!form.electricity || Number(form.electricity) <= 0) e.electricity = 'Enter electricity cost';
      if (!form.marketing || Number(form.marketing) <= 0) e.marketing = 'Enter marketing budget';
    }
    if (s === 3) {
      if (!form.daily_orders || Number(form.daily_orders) <= 0) e.daily_orders = 'Enter daily orders';
      if (!form.contact_name?.trim()) e.contact_name = 'Enter your full name';
      if (!form.contact_phone?.trim()) e.contact_phone = 'Enter your phone number';
      if (!form.contact_email?.trim()) e.contact_email = 'Enter your email';
      if (!form.contact_location?.trim()) e.contact_location = 'Enter your city/location';
    }
    return e;
  };

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    if (errors[key]) setErrors((p) => { const n = { ...p }; delete n[key]; return n; });
  };

  const numSet = (key, val) => {
    const num = val === '' ? '' : Number(val);
    set(key, num);
  };

  const FieldErr = (field) => errors[field] ? (
    <p className="text-xs text-red-500 flex items-center gap-1 mt-1" data-testid={`error-${field}`}>
      <AlertCircle className="h-3 w-3" /> {errors[field]}
    </p>
  ) : null;

  const renderInput = (label, field, prefix, placeholder, type = "number", required = false) => (
    <FormInput
      label={label}
      field={field}
      prefix={prefix}
      placeholder={placeholder}
      type={type}
      required={required}
      value={form[field]}
      onChange={(e) => type === 'number' ? numSet(field, e.target.value) : set(field, e.target.value)}
      error={errors[field]}
    />
  );

  const next = () => {
    const stepErrors = validateStep(step);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      toast.error('Please fill in the required fields');
      return;
    }
    setErrors({});
    if (step < 3) setStep(step + 1);
  };
  const prev = () => { if (step > 0) { setErrors({}); setStep(step - 1); } };

  const handleSubmit = async () => {
    const stepErrors = validateStep(3);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      toast.error('Please fill in the required fields');
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const resolvedArea = form.area === 'Other' ? (form.custom_area?.trim() || '') : form.area;
      const inputs = {
        ...form,
        area: resolvedArea,
        aov: Number(form.aov) || 0,
        seating_capacity: Number(form.seating_capacity) || 0,
        rent: Number(form.rent) || 0,
        deposit: Number(form.deposit) || 0,
        interiors: Number(form.interiors) || 0,
        kitchen_setup: Number(form.kitchen_setup) || 0,
        licenses: Number(form.licenses) || 0,
        opening_inventory: Number(form.opening_inventory) || 0,
        staff_count: Number(form.staff_count) || 0,
        avg_salary: Number(form.avg_salary) || 0,
        electricity: Number(form.electricity) || 0,
        marketing: Number(form.marketing) || 0,
        daily_orders: Number(form.daily_orders) || 0,
        table_turns: Number(form.table_turns) || 1,
      };
      const result = await api.calculateReport(inputs);
      if (result.detail) {
        const code = result.detail?.code;
        if (code === 'FREE_LIMIT_REACHED' || code === 'GUEST_LIMIT_REACHED') {
          setShowPaywall(true);
          return;
        }
      }
      navigate('/results', { state: { report: result } });
    } catch (err) {
      if (err?.message?.includes('free') || err?.message?.includes('Free') || err?.message?.includes('limit') || err?.message?.includes('Upgrade')) {
        setShowPaywall(true);
      } else {
        console.error(err);
        toast.error('Unable to generate report. Please check your inputs.');
      }
    } finally {
      setLoading(false);
    }
  };

  const adjustSplit = (key, newVal) => {
    const keys = ['dine_in_pct', 'delivery_pct', 'takeaway_pct'];
    const others = keys.filter((k) => k !== key);
    const remaining = 100 - newVal;
    const currentOther = form[others[0]] + form[others[1]];
    if (currentOther === 0) {
      set(key, newVal);
      set(others[0], Math.round(remaining / 2));
      set(others[1], remaining - Math.round(remaining / 2));
    } else {
      const ratio0 = form[others[0]] / currentOther;
      const val0 = Math.round(remaining * ratio0);
      setForm((p) => ({ ...p, [key]: newVal, [others[0]]: val0, [others[1]]: remaining - val0 }));
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#E5E5EA]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <button data-testid="back-to-home" onClick={() => navigate('/')}>
            <img src="https://customer-assets.emergentagent.com/job_unit-economics-3/artifacts/z3ko7m68_Cake_Studio.png" alt="ROCH" className="h-14 w-auto" />
          </button>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 md:px-12 py-12 md:py-16">
        {/* Progress */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            {STEP_TITLES.map((t, i) => (
              <button
                key={i}
                data-testid={`step-indicator-${i}`}
                onClick={() => {
                  if (i > step) {
                    const stepErrors = validateStep(step);
                    if (Object.keys(stepErrors).length > 0) {
                      setErrors(stepErrors);
                      toast.error('Complete current step first');
                      return;
                    }
                  }
                  setErrors({});
                  setStep(i);
                }}
                className={`text-xs font-medium transition-colors ${i === step ? 'text-[#1D1D1F]' : 'text-[#86868B]'}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="h-1 bg-[#E5E5EA] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#1D1D1F] transition-all duration-500 rounded-full"
              style={{ width: `${((step + 1) / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className="transition-all duration-500">
          {step === 0 && (
            <div className="space-y-6" data-testid="step-0-content">
              <h2 className="text-2xl md:text-3xl font-medium text-[#1D1D1F] tracking-tight mb-8">Tell us about your concept.</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F] text-sm font-medium">City <span className="text-red-400">*</span></Label>
                  <Select value={form.city} onValueChange={(v) => { set('city', v); set('area', ''); }}>
                    <SelectTrigger data-testid="input-city" className={`bg-white border-[#E5E5EA] rounded-2xl h-12 ${errors.city ? 'border-red-400' : ''}`}><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  {FieldErr("city")}
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F] text-sm font-medium">Area / Locality <span className="text-red-400">*</span></Label>
                  <Select value={form.area} onValueChange={(v) => { set('area', v); if (v !== 'Other') set('custom_area', ''); }} disabled={!form.city}>
                    <SelectTrigger data-testid="input-area" className={`bg-white border-[#E5E5EA] rounded-2xl h-12 ${errors.area ? 'border-red-400' : ''} ${!form.city ? 'opacity-50' : ''}`}>
                      <SelectValue placeholder={form.city ? "Select area" : "Select city first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const builtIn = CITY_AREAS[form.city] || [];
                        const merged = [...builtIn.filter(a => a !== 'Other')];
                        customAreas.forEach(a => { if (!merged.includes(a)) merged.push(a); });
                        merged.sort((a, b) => a.localeCompare(b));
                        merged.push('Other');
                        return merged.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>);
                      })()}
                    </SelectContent>
                  </Select>
                  {form.area === 'Other' && (
                    <div className="mt-2">
                      <Input
                        data-testid="input-custom-area"
                        type="text"
                        value={form.custom_area || ''}
                        onChange={(e) => set('custom_area', e.target.value)}
                        placeholder="Enter the area name (e.g., Araku, Pondicherry Beach Road)"
                        className="bg-white border-[#E5E5EA] rounded-2xl h-12 text-[#1D1D1F]"
                      />
                      <p className="text-[10px] text-[#1D1D1F]/40 mt-1">We'll research this area to evaluate feasibility accurately.</p>
                    </div>
                  )}
                  {FieldErr("area")}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F] text-sm font-medium">Concept Description <span className="text-red-400">*</span></Label>
                <textarea
                  data-testid="input-concept_description"
                  value={form.concept_description}
                  onChange={(e) => set('concept_description', e.target.value)}
                  placeholder="Describe your food business concept in 2-3 lines"
                  className={`w-full bg-white border rounded-2xl p-4 text-sm text-[#1D1D1F] resize-none h-24 focus:outline-none focus:ring-1 focus:ring-[#0071E3] ${errors.concept_description ? 'border-red-400' : 'border-[#E5E5EA]'}`}
                />
                {FieldErr("concept_description")}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F] text-sm font-medium">Cuisine <span className="text-red-400">*</span></Label>
                  <Select value={form.cuisine} onValueChange={(v) => set('cuisine', v)}>
                    <SelectTrigger data-testid="input-cuisine" className={`bg-white border-[#E5E5EA] rounded-2xl h-12 ${errors.cuisine ? 'border-red-400' : ''}`}><SelectValue placeholder="Select cuisine" /></SelectTrigger>
                    <SelectContent>{CUISINES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  {FieldErr("cuisine")}
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F] text-sm font-medium">Business Model</Label>
                  <Select value={form.preferred_model} onValueChange={(v) => set('preferred_model', v)}>
                    <SelectTrigger data-testid="input-preferred_model" className="bg-white border-[#E5E5EA] rounded-2xl h-12"><SelectValue placeholder="Select model type" /></SelectTrigger>
                    <SelectContent>{MODEL_TYPES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {renderInput("Average Order Value", "aov", "₹", "e.g. 500", "number", true)}
                {renderInput("Seating Capacity", "seating_capacity", null, "e.g. 40", "number", true)}
              </div>

              {/* Area Type */}
              <div className="space-y-2">
                <Label className="text-[#1D1D1F] text-sm font-medium">What kind of area is this? <span className="text-red-400">*</span></Label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  {['Residential', 'Commercial', 'High-street', 'Mall', 'Mixed'].map((t) => (
                    <button key={t} type="button" data-testid={`area-type-${t}`}
                      onClick={() => set('area_type', t)}
                      className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${form.area_type === t ? 'bg-[#1D1D1F] text-white border-[#1D1D1F]' : 'bg-white text-[#1D1D1F] border-[#E5E5EA] hover:bg-[#F5F5F7]'}`}>
                      {t}
                    </button>
                  ))}
                </div>
                {errors.area_type && <p className="text-xs text-red-500 flex items-center gap-1 mt-1"><AlertCircle className="h-3 w-3" /> {errors.area_type}</p>}
                <p className="text-[10px] text-[#1D1D1F]/40">Helps estimate footfall, spending behavior, and demand.</p>
              </div>

              {/* Target Audience */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F] text-sm font-medium">Who are you targeting? <span className="text-red-400">*</span></Label>
                  <div className="grid grid-cols-3 gap-2">
                    {['Budget', 'Mid-range', 'Premium'].map((t) => (
                      <button key={t} type="button" data-testid={`spending-${t}`}
                        onClick={() => set('spending_level', t)}
                        className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${form.spending_level === t ? 'bg-[#1D1D1F] text-white border-[#1D1D1F]' : 'bg-white text-[#1D1D1F] border-[#E5E5EA] hover:bg-[#F5F5F7]'}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                  {errors.spending_level && <p className="text-xs text-red-500 flex items-center gap-1 mt-1"><AlertCircle className="h-3 w-3" /> {errors.spending_level}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F] text-sm font-medium">What type of customers? <span className="text-red-400">*</span></Label>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {['Students', 'Professionals', 'Families', 'High-income', 'Mixed'].map((t) => (
                      <button key={t} type="button" data-testid={`audience-${t}`}
                        onClick={() => set('audience_type', t)}
                        className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${form.audience_type === t ? 'bg-[#1D1D1F] text-white border-[#1D1D1F]' : 'bg-white text-[#1D1D1F] border-[#E5E5EA] hover:bg-[#F5F5F7]'}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                  {errors.audience_type && <p className="text-xs text-red-500 flex items-center gap-1 mt-1"><AlertCircle className="h-3 w-3" /> {errors.audience_type}</p>}
                  <p className="text-[10px] text-[#1D1D1F]/40">Helps evaluate if your concept matches the people in your area.</p>
                </div>
              </div>
              <div className="space-y-4 pt-2">
                <Label className="text-[#1D1D1F] text-sm font-medium">Revenue Split</Label>
                {[
                  { label: 'Dine-in', key: 'dine_in_pct' },
                  { label: 'Delivery', key: 'delivery_pct' },
                  { label: 'Takeaway', key: 'takeaway_pct' },
                ].map(({ label, key }) => (
                  <div key={key} className="space-y-1">
                    <div className="flex justify-between text-xs text-[#86868B]">
                      <span>{label}</span>
                      <span>{form[key]}%</span>
                    </div>
                    <Slider
                      data-testid={`slider-${key}`}
                      value={[form[key]]}
                      onValueChange={([v]) => adjustSplit(key, v)}
                      max={100}
                      step={5}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6" data-testid="step-1-content">
              <h2 className="text-2xl md:text-3xl font-medium text-[#1D1D1F] tracking-tight mb-8">Your investment breakdown.</h2>
              <div className="grid grid-cols-2 gap-4">
                {renderInput("Monthly Rent", "rent", "₹", "e.g. 100000", "number", true)}
                {renderInput("Security Deposit", "deposit", "₹", "e.g. 300000", "number", true)}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {renderInput("Interiors & Fitout", "interiors", "₹", "e.g. 1500000", "number", true)}
                {renderInput("Kitchen Equipment", "kitchen_setup", "₹", "e.g. 800000", "number", true)}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {renderInput("Licenses & Permits", "licenses", "₹", "e.g. 100000", "number", true)}
                {renderInput("Opening Inventory", "opening_inventory", "₹", "e.g. 200000", "number", true)}
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F] text-sm font-medium">Working Capital (months of rent)</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    data-testid="slider-working-capital"
                    value={[form.working_capital_months]}
                    onValueChange={([v]) => set('working_capital_months', v)}
                    min={1}
                    max={12}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium text-[#1D1D1F] w-16 text-right">{form.working_capital_months} months</span>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="step-2-content">
              <h2 className="text-2xl md:text-3xl font-medium text-[#1D1D1F] tracking-tight mb-8">Operational details.</h2>
              <div className="grid grid-cols-2 gap-4">
                {renderInput("Staff Count", "staff_count", null, "e.g. 10", "number", true)}
                {renderInput("Avg Monthly Salary", "avg_salary", "₹", "e.g. 15000", "number", true)}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <Label className="text-[#1D1D1F] font-medium">Food Cost %</Label>
                  <span className="text-[#86868B]">{form.food_cost_pct}%</span>
                </div>
                <Slider
                  data-testid="slider-food-cost"
                  value={[form.food_cost_pct]}
                  onValueChange={([v]) => set('food_cost_pct', v)}
                  min={10}
                  max={80}
                  step={1}
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <Label className="text-[#1D1D1F] font-medium">Packaging Cost %</Label>
                  <span className="text-[#86868B]">{form.packaging_pct}%</span>
                </div>
                <Slider
                  data-testid="slider-packaging"
                  value={[form.packaging_pct]}
                  onValueChange={([v]) => set('packaging_pct', v)}
                  min={0}
                  max={15}
                  step={1}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {renderInput("Monthly Electricity", "electricity", "₹", "e.g. 25000", "number", true)}
                {renderInput("Monthly Marketing", "marketing", "₹", "e.g. 30000", "number", true)}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <Label className="text-[#1D1D1F] font-medium">Aggregator Dependence %</Label>
                  <span className="text-[#86868B]">{form.aggregator_dependence_pct}%</span>
                </div>
                <Slider
                  data-testid="slider-aggregator"
                  value={[form.aggregator_dependence_pct]}
                  onValueChange={([v]) => set('aggregator_dependence_pct', v)}
                  min={0}
                  max={100}
                  step={5}
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <Label className="text-[#1D1D1F] font-medium">Menu Categories</Label>
                  <span className="text-[#86868B]">{form.menu_category_count}</span>
                </div>
                <Slider
                  data-testid="slider-menu-cats"
                  value={[form.menu_category_count]}
                  onValueChange={([v]) => set('menu_category_count', v)}
                  min={1}
                  max={25}
                  step={1}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="step-3-content">
              <h2 className="text-2xl md:text-3xl font-medium text-[#1D1D1F] tracking-tight mb-4">Revenue expectations.</h2>

              {/* Dine-in capacity → auto-calculate total orders */}
              {Number(form.seating_capacity) > 0 && Number(form.dine_in_pct) > 0 ? (
                <div className="bg-[#F5F5F7] rounded-2xl p-4 space-y-4">
                  <p className="text-xs font-bold text-[#1D1D1F]">Order Calculator</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <Label className="text-[#1D1D1F] font-medium">Table Turns per Day</Label>
                      <span className="text-[#1D1D1F] font-semibold">{form.table_turns}x</span>
                    </div>
                    <Slider
                      data-testid="slider-table-turns"
                      value={[form.table_turns]}
                      onValueChange={([v]) => {
                        set('table_turns', v);
                        // Auto-calculate total daily orders
                        const covers = Math.round(Number(form.seating_capacity) * v);
                        const dineinPct = form.dine_in_pct || 50;
                        const totalOrders = dineinPct > 0 ? Math.round(covers / (dineinPct / 100)) : covers;
                        set('daily_orders', totalOrders);
                      }}
                      min={1}
                      max={6}
                      step={0.5}
                    />
                    <p className="text-[10px] text-[#1D1D1F]/40">QSR: 3-5 | Cafe: 2-3 | Casual dining: 1.5-2.5 | Fine dining: 1-1.5</p>
                  </div>

                  {/* Auto-calculated breakdown */}
                  {(() => {
                    const covers = Math.round(Number(form.seating_capacity) * form.table_turns);
                    const dineinPct = form.dine_in_pct || 50;
                    const totalOrders = dineinPct > 0 ? Math.round(covers / (dineinPct / 100)) : covers;
                    const deliveryOrders = Math.round(totalOrders * form.delivery_pct / 100);
                    const takeawayOrders = Math.round(totalOrders * form.takeaway_pct / 100);
                    return (
                      <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <div className="bg-white rounded-xl p-3 text-center">
                            <p className="text-lg font-bold text-[#1D1D1F]">{covers}</p>
                            <p className="text-[10px] text-[#1D1D1F]/40 font-medium">Dine-in covers</p>
                            <p className="text-[9px] text-[#1D1D1F]/25">{form.seating_capacity} seats x {form.table_turns}</p>
                          </div>
                          <div className="bg-white rounded-xl p-3 text-center">
                            <p className="text-lg font-bold text-[#1D1D1F]">{deliveryOrders}</p>
                            <p className="text-[10px] text-[#1D1D1F]/40 font-medium">Delivery orders</p>
                            <p className="text-[9px] text-[#1D1D1F]/25">{form.delivery_pct}% of total</p>
                          </div>
                          <div className="bg-white rounded-xl p-3 text-center">
                            <p className="text-lg font-bold text-[#1D1D1F]">{takeawayOrders}</p>
                            <p className="text-[10px] text-[#1D1D1F]/40 font-medium">Takeaway orders</p>
                            <p className="text-[9px] text-[#1D1D1F]/25">{form.takeaway_pct}% of total</p>
                          </div>
                        </div>
                        <div className="bg-[#1D1D1F] rounded-xl p-3 flex items-center justify-between">
                          <span className="text-xs text-white/60 font-medium">Total Daily Orders</span>
                          <span className="text-xl font-bold text-white">{totalOrders}</span>
                        </div>
                        <p className="text-[10px] text-[#1D1D1F]/30">Auto-calculated from seating capacity and revenue split. Adjust table turns or edit manually below.</p>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* Cloud kitchen / no seating — manual input */
                <div>
                  {renderInput("Total Daily Orders", "daily_orders", null, "e.g. 80", "number", true)}
                  <p className="text-[10px] text-[#1D1D1F]/40 mt-1">No seating capacity — enter your expected daily order volume.</p>
                </div>
              )}

              {/* Manual override */}
              {Number(form.seating_capacity) > 0 && Number(form.dine_in_pct) > 0 && (
                <div>
                  {renderInput("Total Daily Orders (override)", "daily_orders", null, "Auto-calculated above", "number", true)}
                  <p className="text-[10px] text-[#1D1D1F]/40 mt-1">Edit this to override the auto-calculation if needed.</p>
                </div>
              )}

              {/* Operating days */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <Label className="text-[#1D1D1F] font-medium">Operating Days / Month <span className="text-red-400">*</span></Label>
                  <span className="text-[#1D1D1F] font-semibold">{form.operating_days} days</span>
                </div>
                <Slider
                  data-testid="slider-operating-days"
                  value={[form.operating_days]}
                  onValueChange={([v]) => set('operating_days', v)}
                  min={15}
                  max={31}
                  step={1}
                />
                <p className="text-[10px] text-[#1D1D1F]/40">Most restaurants: 26-30. Weekday-only: 22-24.</p>
              </div>

              {/* Monthly revenue preview */}
              {Number(form.daily_orders) > 0 && Number(form.aov) > 0 && (
                <div className="bg-[#1D1D1F] rounded-2xl p-4 text-center">
                  <p className="text-[10px] text-white/40 font-semibold uppercase tracking-wider mb-1">Projected Gross Revenue</p>
                  <p className="text-2xl font-bold text-white">
                    {(() => {
                      const rev = Number(form.daily_orders) * Number(form.aov) * form.operating_days;
                      return rev >= 100000 ? `₹${(rev/100000).toFixed(1)}L/month` : `₹${(rev/1000).toFixed(0)}K/month`;
                    })()}
                  </p>
                  <p className="text-[10px] text-white/30 mt-1">{form.daily_orders} orders x ₹{form.aov} AOV x {form.operating_days} days</p>
                </div>
              )}

              {/* Personal Details — mandatory for report access */}
              <div className="bg-[#F5F5F7] rounded-2xl p-4 space-y-3 mt-2">
                <p className="text-xs font-bold text-[#1D1D1F]">Your Details <span className="text-[10px] font-medium text-[#1D1D1F]/40">(required to generate report)</span></p>
                <div className="grid grid-cols-2 gap-3">
                  {renderInput("Full Name", "contact_name", null, "e.g. Rohit Kumar", "text", true)}
                  {renderInput("Phone Number", "contact_phone", null, "e.g. 9876543210", "tel", true)}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {renderInput("Email", "contact_email", null, "e.g. you@email.com", "email", true)}
                  {renderInput("Your City / Location", "contact_location", null, "e.g. Hyderabad", "text", true)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-12">
          {step > 0 ? (
            <Button
              data-testid="prev-step-btn"
              onClick={prev}
              variant="ghost"
              className="text-[#86868B] hover:text-[#1D1D1F] rounded-full px-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Back
            </Button>
          ) : (
            <div />
          )}
          {step < 3 ? (
            <Button
              data-testid="next-step-btn"
              onClick={next}
              className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full px-8 py-3 transition-all duration-500 active:scale-95"
            >
              Continue
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
            </Button>
          ) : (
            user ? (
              <Button
                data-testid="submit-assessment-btn"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full px-8 py-3 transition-all duration-500 active:scale-95"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Get My Score
              </Button>
            ) : (
              <Button
                data-testid="login-to-submit-btn"
                onClick={login}
                className="bg-[#0071E3] text-white hover:bg-[#0077ED] rounded-full px-8 py-3 transition-all duration-500 active:scale-95"
              >
                Sign In to Get Your Score
              </Button>
            )
          )}
        </div>
      </div>

      {/* Free Limit Paywall Overlay */}
      {showPaywall && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6" data-testid="paywall-overlay">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-8 md:p-10 shadow-[0_32px_64px_rgba(0,0,0,0.2)]">
            <div className="text-center mb-8">
              <Lock className="h-10 w-10 text-[#1D1D1F]/20 mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="text-xl md:text-2xl font-bold text-[#1D1D1F] mb-2">Free Report Limit Reached</h2>
              <p className="text-sm text-[#1D1D1F]/50 font-medium max-w-md mx-auto">
                {user ? "You've used your free report. Choose a plan to get full financial analysis, AI insights, and more."
                       : "Sign in to get one free report, or choose a plan for full access."}
              </p>
            </div>

            {!user && (
              <div className="text-center mb-6">
                <Button onClick={login} className="bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E5E5EA] rounded-full px-8 py-3 font-semibold mb-2">
                  Sign In for 1 Free Report
                </Button>
                <p className="text-xs text-[#1D1D1F]/30">Or choose a plan below</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { plan: 'basic', name: 'Report', price: '499', tagline: 'Understand your numbers',
                  features: ['Complete financial breakdown', 'All risk flags explained', 'AI-powered fix suggestions', 'PDF report download'] },
                { plan: 'scenario_pack', name: 'Report + Scenarios', price: '999', popular: true, tagline: 'Test before you commit',
                  features: ['Everything in Report', '3 what-if scenario tests', 'Side-by-side comparison', 'Find your best-case numbers'] },
                { plan: 'pro', name: 'Full Analysis', price: '2,999', tagline: 'Investor-ready clarity',
                  features: ['Everything in Scenarios', '5 scenario credits', 'Benchmark comparisons', 'Priority consultation access'] },
              ].map((tier) => (
                <div key={tier.plan} className={`rounded-2xl border p-5 ${tier.popular ? 'border-[#1D1D1F] bg-[#FBFBFD]' : 'border-[#E5E5EA]'}`}>
                  {tier.popular && <span className="text-[9px] font-bold text-white bg-[#1D1D1F] rounded-full px-2.5 py-0.5 mb-3 inline-block">POPULAR</span>}
                  <h4 className="text-sm font-bold text-[#1D1D1F]">{tier.name}</h4>
                  <p className="text-[10px] text-[#1D1D1F]/40 font-medium mb-2">{tier.tagline}</p>
                  <div className="text-2xl font-bold text-[#1D1D1F] mb-4">₹{tier.price}</div>
                  <ul className="space-y-2 mb-5">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[11px] text-[#1D1D1F]/60 font-medium">
                        <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} />{f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => { if (!user) { login(); return; } initiatePayment(tier.plan, null, user, () => { setShowPaywall(false); handleSubmit(); }); }}
                    className={`w-full rounded-full py-2.5 text-xs font-semibold ${tier.popular ? 'bg-[#1D1D1F] text-white hover:bg-[#434344]' : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E5E5EA]'}`}
                  >
                    Get {tier.name}
                  </Button>
                </div>
              ))}
            </div>

            <button onClick={() => setShowPaywall(false)} className="w-full text-center text-xs text-[#1D1D1F]/30 mt-6 hover:text-[#1D1D1F]/50">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
